import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react'
import { reaction, runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'

import { useApi } from '../../hooks/storeHook'
import { useMessages } from '../../hooks/snackbarHooks'

import { RouteTabItem } from '../RoutingTabs'
import { RoutingTabsSwitch } from '../RoutingTabsSwitch'
import { MsStoresLoadingProps } from './StoreReturnsSettings'
import { CompanySettings, StoreAccountMsData } from './CompanySettings'

import { NonEmptyArray } from '../../common/tsUtils'
import { useLocalObservableOptional } from '../../common/mobxUtils'
import { appRoutes, IntegrationRouteParams } from '../../common/appRoutes'

import { StoreAccountModel } from '../../types/accountProps'
import { integrationKeyByType } from '../../types/integrationTypeUtils'

import { IntegrationType } from '../../server/mpsklad_core/Entity/IntegrationType'
import { isAppTypeMpSklad } from '../../common/isAppTypeMpSklad'
import { MoySkladStoreModel } from '../../server/mpsklad_core/Models/MoySkladStoreModel'
import { EditStoreAccountMsDataModel } from '../../server/mpsklad_core/Models/EditStoreAccountMsDataModel'
import { Box } from '@material-ui/core'
import { Route } from 'react-router-dom'

export type StoreSettingsPageProps<TAccount extends StoreAccountModel> = {
  account: TAccount | undefined

  integrationType: IntegrationType & TAccount['integrationType']

  onSubmitMsData: (data: EditStoreAccountMsDataModel) => Promise<void>

  allowSuppliesSettings: boolean

  ApiSettingsComponent: FunctionComponent

  OrdersSettingsComponent: FunctionComponent<{account: TAccount}>

  WarehousesSettingsComponent: FunctionComponent<{account: TAccount}>

  ReturnsSettingsComponent: FunctionComponent<MsStoresLoadingProps>

  CommissionsSettingsComponent: FunctionComponent<{account: TAccount}>

  PricesSettingsComponent: FunctionComponent<{account: TAccount}>

  SuppliesSettingsComponent: FunctionComponent<{account: TAccount} & MsStoresLoadingProps>
}

export const StoreSettingsPage = observer(
  <TAccount extends StoreAccountModel>
  ({
     account,
     integrationType,
     allowSuppliesSettings,
     onSubmitMsData,
     ApiSettingsComponent,
     CommissionsSettingsComponent,
     OrdersSettingsComponent,
     PricesSettingsComponent,
     ReturnsSettingsComponent,
     WarehousesSettingsComponent,
     SuppliesSettingsComponent
   }: StoreSettingsPageProps<TAccount>) => {
    const api = useApi()
    const {showError} = useMessages()

    const msContainer = useLocalObservableOptional<StoreAccountMsData>()
    const [msStores, setMsStores] = useState<MoySkladStoreModel[]>()

    useEffect(() =>
        reaction(
          () => msContainer.dataOrNull?.organization?.value?.id,
          async () => {
            if (!msContainer.dataOrNull) {
              return
            }

            const organizationId = msContainer.dataOrNull.organization.value?.id

            if (!organizationId) {
              return
            }

            const newOrgAccounts = await api.userSync.getMoySkladOrganizationAccounts(organizationId)

            runInAction(() => {
              msContainer.data.organizationAccount.options = newOrgAccounts
              msContainer.data.organizationAccount.value = null
            })
          }),
      [api, msContainer])

    const settingsRoute = appRoutes.Store.settings

    const loadMsData = useCallback(async () => {
      if (!account) {
        return
      }

      try {
        const {organizations, counterparties, contracts, salesChannels, groups, organizationAccounts, projects} =
          await api.userSync.getMoySkladData(account.integrationType, account.id)

        if (organizations.length === 0) {
          showError('Нет организаций в МоёмСкладе')
          return
        }

        if (counterparties.length === 0) {
          showError('Нет контрагентов в МоёмСкладе с типом "Юридическое лицо"')
          return
        }

        msContainer.dataOrNull = {
          organization: {
            options: organizations,
            value: organizations.find(_ => _.id === account.msOrganization) ?? null,
            searchTerm: undefined,
            isSearchLoading: false
          },
          counterparty: {
            options: counterparties,
            value: counterparties.find(_ => _.id === account.msCounterparty) ?? null,
            searchTerm: undefined,
            isSearchLoading: false
          },
          contract: {
            options: contracts,
            value: contracts.find(_ => _.id === account.msContractId) ?? null,
            searchTerm: undefined,
            isSearchLoading: false
          },
          salesChannel: {
            options: salesChannels,
            value: salesChannels.find(_ => _.id === account.msSalesChannelId) ?? null,
            searchTerm: undefined,
            isSearchLoading: false
          },
          group: {
            options: groups,
            value: groups.find(_ => _.id === account.msGroupId) ?? null,
            searchTerm: undefined,
            isSearchLoading: false
          },
          organizationAccount: {
            options: organizationAccounts,
            value: organizationAccounts.find(_ => _.id === account.msOrganizationAccountId) ?? null,
            searchTerm: undefined,
            isSearchLoading: false
          },
          project: {
            options: projects,
            value: projects.find(_ => _.id === account.msProjectId) ?? null,
            searchTerm: undefined,
            isSearchLoading: false
          },
          get formData() {
            return {
              accountId: account.id,
              msOrganization: this.organization.value?.id || '',
              msCounterparty: this.counterparty.value?.id || '',
              msContractId: this.contract.value?.id || undefined,
              msSalesChannelId: this.salesChannel.value?.id || undefined,
              msGroupId: this.group.value?.id || undefined,
              msOrganizationAccountId: this.organizationAccount.value?.id || undefined,
              msProjectId: this.project.value?.id || undefined
            }
          }
        }
      } catch (e) {
        showError('Не удалось загрузить данные из МоегоСклада')
      }
    }, [account, api.userSync, msContainer, showError])

    const loadMsStores = useCallback(async () => {
      if (!account) {
        return
      }
      try {
        setMsStores(await api.userSync.getMoySkladStores())
      } catch (e) {
        showError('Не удалось загрузить склады МоегоСклада')
      }
    }, [account, api.userSync, showError])

    const routes =
      useMemo(() => {
          const routeParams: IntegrationRouteParams = {
            accountId: account?.id ?? 0,
            integration: integrationKeyByType[integrationType]
          }

          const result = [{
            title: 'API',
            template: settingsRoute.api.template,
            path: settingsRoute.api.makeUrl(routeParams),
            render: () => <ApiSettingsComponent/>
          }, {
            title: 'Компания',
            template: settingsRoute.company.template,
            path: settingsRoute.company.makeUrl(routeParams),
            isDisabled: !account,
            render: () => account &&
                          <CompanySettings msContainer={msContainer} loadMsData={loadMsData} onSubmit={onSubmitMsData}/>
          }, {
            title: 'Заказы',
            template: settingsRoute.orders.template,
            path: settingsRoute.orders.makeUrl(routeParams),
            isDisabled: !account,
            // TODO: Replace account prop drilling with route hooks, up and down the component stack
            render: () => account && <OrdersSettingsComponent account={account}/>
          }, {
            title: 'Склады',
            template: settingsRoute.warehouses.template,
            path: settingsRoute.warehouses.makeUrl(routeParams),
            isDisabled: !account,
            render: () => account && <WarehousesSettingsComponent account={account}/>
          }, {
            title: 'Возвраты',
            template: settingsRoute.returns.template,
            path: settingsRoute.returns.makeUrl(routeParams),
            isDisabled: !account,
            render: () => account && <ReturnsSettingsComponent msStores={msStores} loadMsStores={loadMsStores}/>
          }, {
            title: 'Комиссии',
            template: settingsRoute.commissions.template,
            path: settingsRoute.commissions.makeUrl(routeParams),
            isDisabled: !account,
            render: () => account && <CommissionsSettingsComponent account={account}/>
          }, {
            title: 'Цены',
            template: settingsRoute.prices.template,
            path: settingsRoute.prices.makeUrl(routeParams),
            isDisabled: !account,
            render: () => account && <PricesSettingsComponent account={account}/>
          }] satisfies NonEmptyArray<RouteTabItem>

          if (allowSuppliesSettings && account?.allowOptionAutoCreateMoveDocsInMoySklad) {
            result.push({
              title: 'Поставки',
              template: settingsRoute.supplies.template,
              path: settingsRoute.supplies.makeUrl(routeParams),
              render: () => <SuppliesSettingsComponent account={account} msStores={msStores} loadMsStores={loadMsStores}/>
            })
          }

          return result
        },
        [ApiSettingsComponent, CommissionsSettingsComponent, OrdersSettingsComponent,
          PricesSettingsComponent, ReturnsSettingsComponent, WarehousesSettingsComponent, SuppliesSettingsComponent,
          account, integrationType, allowSuppliesSettings, msContainer, msStores,
          loadMsData, loadMsStores, onSubmitMsData,
          settingsRoute
        ])

    if (!isAppTypeMpSklad()) {
      return (
        <Box style={{padding: '37px 6px'}}>
          <Route
            path={appRoutes.Store.settings.template}
            render={() => <ApiSettingsComponent/>}
          />
        </Box>
      )
    }

    return <RoutingTabsSwitch routeTabs={routes}/>
  })
